import { useState } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  useTheme,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  styled,
} from '@mui/material';
import { X } from 'lucide-react';
import ClientForm from './steps/ClientForm';
import CompanyForm from './steps/CompanyForm';
import SubscriptionForm from './steps/SubscriptionForm';
import SummaryView from './steps/SummaryView';
import { useOrganizationForm } from '../../../hooks/organisation/useOrganizationForm';
import { LAYOUT } from '../../../constants/layout';

interface OrganizationAddViewProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const steps = ['Client Information', 'Company Details', 'Subscription', 'Summary'];

const CustomStepConnector = styled(StepConnector)(({ theme }) => ({
  '& .MuiStepConnector-line': {
    borderColor: theme.palette.divider,
  },
  '&.Mui-active, &.Mui-completed': {
    '& .MuiStepConnector-line': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

export default function OrganizationAddView({
  open,
  onClose,
  onSuccess,
}: OrganizationAddViewProps) {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const { formData, updateFormData, handleSubmit, isLoading } = useOrganizationForm();

  const handleNext = () => {
    setActiveStep(prevStep => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };

  const handleFormSubmit = async () => {
    try {
      await handleSubmit();
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to submit organization:', error);
    }
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <ClientForm data={formData.client} onUpdate={updateFormData} onNext={handleNext} />;
      case 1:
        return (
          <CompanyForm
            data={formData.company}
            onUpdate={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 2:
        return (
          <SubscriptionForm
            data={formData}
            onUpdate={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <SummaryView
            data={formData}
            onSubmit={handleFormSubmit}
            onBack={handleBack}
            isLoading={isLoading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: {
            xs: '100%',
            sm: `calc(100% - ${LAYOUT.DRAWER_WIDTH}px)`,
            md: '70%',
          },
          p: 0,
          backgroundColor: theme.palette.background.default,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          Add Organization
        </Typography>
        <IconButton onClick={onClose} size="small">
          <X size={18} />
        </IconButton>
      </Box>

      {/* Steps Indicator */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Stepper
          activeStep={activeStep}
          connector={<CustomStepConnector />}
          sx={{
            '& .MuiStepLabel-label': {
              fontSize: '0.875rem',
              '&.Mui-active': {
                color: theme.palette.primary.main,
                fontWeight: 600,
              },
            },
            '& .MuiStepIcon-root': {
              width: 28,
              height: 28,
              '&.Mui-active': {
                color: theme.palette.primary.main,
              },
              '&.Mui-completed': {
                color: theme.palette.primary.main,
              },
            },
          }}
        >
          {steps.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Form Content */}
      <Box
        sx={{
          flex: 1,
          p: 3,
          overflowY: 'auto',
          height: `calc(100% - ${LAYOUT.TOOLBAR_HEIGHT * 2}px)`,
        }}
      >
        {getStepContent(activeStep)}
      </Box>
    </Drawer>
  );
}
